import { useEffect, useRef, useState, type ReactNode } from 'react';
import { sx } from './designStyles';

/**
 * The V9 tab row (design `more()` in renderVals, change log "V9 started"): the tabs a page
 * keeps stay in one row and the rest sit in a "More" dropdown at the end of it. The button
 * reads "More: X" while one of the folded tabs is open, styled like the open tab. A row
 * with only one tab past the kept ones shows it inline, as the design does. The menu closes
 * on a click outside it and on Escape.
 */
export interface MoreTab<K extends string> {
  key: K;
  /** What the tab shows (may carry a count, e.g. "Actions · 3"). */
  label: ReactNode;
  /** The plain name, for "More: X" and the menu. Defaults to the key. */
  name?: string;
}

/** The menu item style, from the design. */
const ITEM = (on: boolean) =>
  'display:flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;font-size:16px;text-decoration:none;white-space:nowrap;' +
  (on ? 'font-weight:700;color:#1D3F6B;background:rgba(79,138,214,.13);' : 'font-weight:500;color:#22272E;');
const MENU =
  'position:absolute;top:calc(100% + 4px);left:0;z-index:60;display:flex;flex-direction:column;gap:2px;min-width:220px;padding:6px;border-radius:10px;background:#FCFAF4;border:1px solid rgba(34,39,46,.16);box-shadow:0 8px 24px rgba(34,39,46,.14);';

/** Splits the tabs the way the design does: kept ones inline, the rest folded. */
export function splitTabs<K extends string>(tabs: readonly MoreTab<K>[], keep: readonly K[]): { shown: MoreTab<K>[]; folded: MoreTab<K>[] } {
  if (tabs.length <= keep.length + 1) return { shown: [...tabs], folded: [] };
  return { shown: tabs.filter((t) => keep.includes(t.key)), folded: tabs.filter((t) => !keep.includes(t.key)) };
}

export function TabMore<K extends string>({
  tabs,
  keep,
  value,
  onChange,
  tabStyle,
  label,
  rowStyle,
  before,
}: {
  tabs: readonly MoreTab<K>[];
  /** The tabs that always stay in the row, in the order the page lists them. */
  keep: readonly K[];
  value: K;
  onChange: (k: K) => void;
  /** The page's own tab style (the More button copies it). */
  tabStyle: (on: boolean) => string;
  /** Accessible name of the tab row. */
  label: string;
  rowStyle: string;
  /** Anything the row carries before the tabs (Atlas: the reach and memory button). */
  before?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const { shown, folded } = splitTabs(tabs, keep);
  const act = folded.find((t) => t.key === value);
  const nm = (t: MoreTab<K>) => t.name ?? t.key;

  useEffect(() => {
    if (!open) return;
    const close = (ev: Event) => {
      if (ev.type === 'keydown') {
        if ((ev as KeyboardEvent).key === 'Escape') setOpen(false);
        return;
      }
      if (wrap.current && ev.target instanceof Node && wrap.current.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', close);
    document.addEventListener('mousedown', close);
    return () => {
      document.removeEventListener('keydown', close);
      document.removeEventListener('mousedown', close);
    };
  }, [open]);

  return (
    <span role="tablist" aria-label={label} style={sx(rowStyle)}>
      {before}
      {shown.map((t) => (
        <a
          key={t.key}
          href="#"
          role="tab"
          aria-selected={t.key === value}
          onClick={(e) => {
            e.preventDefault();
            onChange(t.key);
          }}
          style={sx(tabStyle(t.key === value))}
        >
          {t.label}
        </a>
      ))}
      {folded.length > 0 && (
        <span ref={wrap} style={sx('position:relative;display:inline-flex;flex:0 0 auto;')}>
          <a
            href="#"
            data-testid="tab-more"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={(e) => {
              e.preventDefault();
              setOpen((o) => !o);
            }}
            style={sx(tabStyle(!!act) + 'cursor:pointer;')}
          >
            {(act ? 'More: ' + nm(act) : 'More') + ' ▾'}
          </a>
          {open && (
            <div role="menu" aria-label={label + ': more'} style={sx(MENU)}>
              {folded.map((t) => (
                <a
                  key={t.key}
                  href="#"
                  role="menuitem"
                  aria-current={t.key === value || undefined}
                  className="hx-quiet"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen(false);
                    onChange(t.key);
                  }}
                  style={sx(ITEM(t.key === value))}
                >
                  {nm(t)}
                </a>
              ))}
            </div>
          )}
        </span>
      )}
    </span>
  );
}
